import React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import { Lock } from "@mui/icons-material";
import { useApiClient } from "../../../api";

const promptStyle = {
  maxWidth: "100%",
  height: "auto",
  "& .MuiChip-label": {
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    textAlign: "left",
    py: 1,
  },
};

export const SuggestedPrompts = ({ onSelect, context }) => {
  const apiClient = useApiClient();
  const [prompts, setPrompts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  // Determine category based on context
  let category = "default";
  if (context?.assignment_id) {
    category = "assignment";
  } else if (context?.class_id) {
    category = "class";
  }

  React.useEffect(() => {
    const fetchPrompts = async () => {
      try {
        setLoading(true);
        const data = await apiClient.assistant.getSuggestedPrompts(category);
        setPrompts(data || []);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch suggested prompts:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPrompts();
  }, [category]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          py: 2,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          py: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Unable to load suggested prompts
        </Typography>
      </Box>
    );
  }

  if (prompts.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        justifyContent: "center",
        px: 2,
        pb: 2,
      }}
    >
      {prompts.map((prompt) => {
        // Wrap locked prompts with custom lock icon
        if (prompt.isAccessible === false) {
          return (
            <Tooltip
              key={prompt.id}
              title={prompt.tierUpgradeHint || "Upgrade to unlock this feature"}
              arrow
            >
              <Box
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  maxWidth: "100%",
                }}
              >
                <Chip
                  label={prompt.text}
                  variant="outlined"
                  sx={{
                    ...promptStyle,
                    opacity: 0.6,
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                />
                <Lock
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    fontSize: 16,
                    color: "primary.main",
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    borderRadius: "50%",
                    padding: 0.5,
                  }}
                />
              </Box>
            </Tooltip>
          );
        }

        return (
          <Chip
            key={prompt.id}
            label={prompt.text}
            onClick={() => onSelect(prompt.text)}
            clickable
            variant="outlined"
            sx={{
              ...promptStyle,
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
          />
        );
      })}
    </Box>
  );
};
